import { z } from "zod";
import { localeSchema, sessionAuthMethodSchema, userRoleSchema, type User } from "@ferrivia/api-contract";

const STORAGE_KEY = "ferrivia.cached-session";
const CACHE_VERSION = 1;
// Backstop only — the real bound is the token's own expiry, checked
// alongside this in useSessionState (packages/auth-web). This just keeps a
// forgotten cache from surviving indefinitely if the token itself has no
// declared expiry.
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

// Exactly `id`, `role`, `locale`, `authMethod`, `onboardingComplete` — no
// `email`, `phone`, `preferredName`, no eID identity fields, no onboarding
// answers. See the compliance position in the Phase 5 plan / this PR's
// description: the bearer token already sits unencrypted in the same
// localStorage, so this adds ~no confidentiality delta for the fields it
// does carry, but direct identifiers like these would be a real one, so
// they're excluded on purpose.
const cachedSessionSchema = z.object({
  version: z.literal(CACHE_VERSION),
  cachedAt: z.number(),
  id: z.string(),
  role: userRoleSchema,
  locale: localeSchema.nullable(),
  authMethod: sessionAuthMethodSchema,
  onboardingComplete: z.boolean(),
});

export type CachedSession = z.infer<typeof cachedSessionSchema>;

export function readCachedSession(): CachedSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = cachedSessionSchema.safeParse(JSON.parse(raw));
    if (!parsed.success) return null;
    if (Date.now() - parsed.data.cachedAt > MAX_AGE_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

// User-id namespacing is a correctness measure, not a security boundary —
// same origin, same localStorage, no partition. What carries the weight:
// no Art. 9 data, no direct identifiers, lifetime bounded by session
// expiry, purge on sign-out and on 401 (see cachedSessionSeed in App.tsx).
export function writeCachedSession(user: User, onboardingComplete: boolean): void {
  const payload: CachedSession = {
    version: CACHE_VERSION,
    cachedAt: Date.now(),
    id: user.id,
    role: user.role,
    locale: user.locale,
    authMethod: user.authMethod,
    onboardingComplete,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage unavailable — nothing to boot from cached next time.
  }
}

export function clearCachedSession(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // see writeCachedSession
  }
}

// `email`/`phone`/`preferredName` are deliberately synthesised as `null`
// rather than carried — they're the fields excluded from the cache on
// purpose (see the schema comment above). The profile sheet reads them from
// `user` to show and edit, so a cache-hydrated shell briefly renders those
// fields blank until the real `/me` verifies and the session state updates —
// the same one-round-trip gap `locale`/`authMethod` never have because they
// *are* cached.
export function toSessionSeed(cached: CachedSession): { user: User } {
  return {
    user: {
      id: cached.id,
      role: cached.role,
      email: null,
      phone: null,
      preferredName: null,
      locale: cached.locale,
      authMethod: cached.authMethod,
    },
  };
}
